import React, { useEffect } from "react";
import { Divider, Grid, Typography } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { MTextField, ResponsiveFormContainer } from "@app/components";
import { toExponentialUppercase } from "@app/utils/functions";

interface TritioFormProps {
  mode: MODES_OPTIONS;
}

const TritioForm = ({ mode }: TritioFormProps) => {
  const { setValue, control } = useFormContext();
  const isView = mode === MODES_OPTIONS.VIEW;

  const primaryValue = parseFloat(useWatch({ control, name: "evh3_d" }) || 0);
  const massValue = parseFloat(useWatch({ control, name: "emh3_d" }) || 0);

  useEffect(() => {
    if (!isNaN(primaryValue)) {
      const primaryValueInMicro = primaryValue * 1e-6;
      setValue("evh3_c", toExponentialUppercase(primaryValueInMicro, 4));
      setValue(
        "evh3",
        toExponentialUppercase(Math.pow(primaryValueInMicro / 2, 2) * 2, 4)
      );
      const calcResult_bq_l = primaryValue * 1e-3;
      setValue("result_bq_l", toExponentialUppercase(calcResult_bq_l, 4));

      const cal_k40_calibration_source_vol_uncertainty =
        Math.pow(calcResult_bq_l / 2, 2) * 2;
      setValue(
        "k40_calibration_source_vol_uncertainty",
        toExponentialUppercase(cal_k40_calibration_source_vol_uncertainty, 4)
      );
      setValue(
        "k2_calibration_source_vol_uncertainty",
        toExponentialUppercase(primaryValue, 4)
      );

      setValue(
        "k1_u2_vol_uncertainty",
        toExponentialUppercase(Math.pow(primaryValue / 2, 2) * 2, 4)
      );
    }

    if (!isNaN(massValue)) {
      const massValueInMicro = massValue * 1e-6;
      setValue("emh3_c", toExponentialUppercase(massValueInMicro, 2));
      setValue(
        "emh3",
        toExponentialUppercase(Math.pow(massValueInMicro / 2, 2) * 2, 4)
      );
      setValue(
        "k2_calibration_source_mass_uncertainty",
        toExponentialUppercase(massValue * 1e-3, 2)
      );
      setValue(
        "k1U2_g_calibration_source_mass_uncertainty",
        toExponentialUppercase(Math.pow((massValue * 1e-3) / 2, 2) * 2, 4)
      );
    }
  }, [primaryValue, massValue, setValue]);

  return (
    <>
      <Divider />
      <Grid item xs={12} my={3} mt={3}>
        <Typography variant="tagText">{"Tritio"}</Typography>
      </Grid>
      <Grid item xs={12} my={3}>
        <Typography variant="tagText">
          {"Muestras de aguas. Resultado en Bq/m3"}
        </Typography>
      </Grid>
      <ResponsiveFormContainer columns={2}>
        <MTextField
          name="evh3_d"
          label="Incertidumbre volumen muestra para k=2 (ml) "
          type="number"
          fullWidth
          validation={{ required: "El valor es requerido" }}
          helperText="(*) Aplica la incertidumbre de la pipeta automática de 5ml"
          disabled={isView}
        />
        <MTextField
          name="evh3_c"
          label="k=2 (m3)"
          type="number"
          fullWidth
          disabled
        />
        <MTextField
          name="evh3"
          label="k=1 u2 (m3)2"
          type="number"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>

      <Grid item xs={12} my={3}>
        <Typography variant="tagText">
          {"Muestras de agua. Resultado en Bq/l y Bq/kg"}
        </Typography>
      </Grid>
      <ResponsiveFormContainer columns={2}>
        <MTextField
          name="result_bq_l"
          label="k=2 (l) (kg)"
          type="number"
          fullWidth
          disabled
        />
        <MTextField
          name="k40_calibration_source_vol_uncertainty"
          label="k=1 u2 (l)2 u2 (kg)2 "
          type="number"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>

      <Grid item xs={12} my={3}>
        <Typography variant="tagText">
          {"Muestras de agua. Resultado en Bq/g"}
        </Typography>
      </Grid>
      <ResponsiveFormContainer columns={2}>
        <MTextField
          name="k2_calibration_source_vol_uncertainty"
          label="k=2 (g)"
          type="number"
          fullWidth
          disabled
        />
        <MTextField
          name="k1_u2_vol_uncertainty"
          label="k=1 u2 (g)2"
          type="number"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>

      <Grid item xs={12} my={3}>
        <Typography variant="tagText">
          {"Muestras sólidas. Resultado en Bq/Kg"}
        </Typography>
      </Grid>
      <ResponsiveFormContainer columns={2}>
        <MTextField
          name="emh3_d"
          label="Incertidumbre de la masa muestra para k=2 (mg)"
          helperText="(*) Aplica la incertidumbre máxima de la balanza analítica con 100g"
          type="number"
          fullWidth
          validation={{ required: "El valor es requerido" }}
          disabled={isView}
        />
        <MTextField
          name="emh3_c"
          label="k=2 (kg)"
          type="number"
          fullWidth
          disabled
        />
        <MTextField
          name="emh3"
          label="k=1 u2 (kg)2"
          type="number"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>

      <Grid item xs={12} my={3}>
        <Typography variant="tagText">
          {"Unidades de masa. Resultado en Bq/g"}
        </Typography>
      </Grid>
      <ResponsiveFormContainer columns={2}>
        <MTextField
          name="k2_calibration_source_mass_uncertainty"
          label="k=2 (g)"
          type="number"
          fullWidth
          disabled
        />
        <MTextField
          name="k1U2_g_calibration_source_mass_uncertainty"
          label="k=1 u2 (g)2"
          type="number"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>
    </>
  );
};

export default TritioForm;
